// PackageDetails.jsx
import { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import FieldsTable from "@/Components/Tables/FieldsTable";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX,LuCheck } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import {
  useCreatePackageDetailsMutation,
  useUpdatePackageDetailsMutation,
  useDeletePackageDetailsMutation,
  useGetPackageDetailsByMasterIdQuery,
} from "@/redux/ExportPage/CreateNewJob/Container/ContainerPackageSlice";
// import { useGetQuantityQuery } from "@/redux/ExportPage/CreateNewJob/Shipment/ShipmentMainSlice";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import { BiSolidError } from "react-icons/bi";
import { useUser } from "@/Context/UserContext";
import TableGenerator from "@/Utils/FormGenerator/TableGenerator";
import FormDialog from "@/Components/Dialog/FormDialog";
import HighSeaSellerSchema from "@/Config/ImportJob/CreateNewJob/General/HighSeaSellerSchema";
import { useCreateHighSeaSellerMutation } from "@/redux/ImportPage/CreateNewJob/General/HighSeaSellerApiSlice";
import { importHookMap } from "@/redux/importHookMap";
import { useGetHighSeaSellerQuery } from "@/redux/ImportPage/CreateNewJob/General/HighSeaSellerApiSlice";
import {
  useUpdateHighSeaSellerMutation,
  useDeleteHighSeaSellerMutation,
} from "@/redux/ImportPage/CreateNewJob/General/HighSeaSellerApiSlice";

export default function ImportHighSeaSeller({ onClose, packingdetails }) {
  const [rows, setRows] = useState([]);
  const [createPackageDetails] = useCreatePackageDetailsMutation();
  const [updatePackageDetails] = useUpdatePackageDetailsMutation();
  const [deletePackageDetails] = useDeletePackageDetailsMutation();
  // const { data: quantityOptions = [] } = useGetQuantityQuery();
  const [loading, setLoading] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { importerMasterId, formData, setFormData } = useUser();
  const {
    data: highSeaSellerData,
    isFetching,
    error,
    refetch: refetchHighSeaSeller,
  } = useGetHighSeaSellerQuery({ id: importerMasterId });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [formDialogData, setFormDialogData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [highSeaSellerId, setHighSeaSellerId] = useState(null);
  const formRef = useRef();
  const [create] = useCreateHighSeaSellerMutation();
  const [update] = useUpdateHighSeaSellerMutation();
  const [deleteFn] = useDeleteHighSeaSellerMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  // 🔹 Save button disabled if any row missing required fields

  useEffect(() => {
    if (error) {
      // ✅ clear data on error response (like 400)
      setFormData((prev) => ({
        ...prev,
        highSeaSellerData: [],
      }));
      return;
    }

    if (!isFetching && Array.isArray(highSeaSellerData)) {
      const dataWithSrNo = highSeaSellerData.map((item, index) => ({
        ...item,
        srNo: index + 1,
        id: item.importerHighSeaSellerId,
      }));

      setFormData((prev) => ({
        ...prev,
        highSeaSellerData: dataWithSrNo,
      }));
    }
  }, [highSeaSellerData, isFetching, error]);

  const handleActions = () => {};

  const handleCloseForm = () => {
    setIsEditing(false);
    setFormDialogData({});
    setShowForm(false);
  };

  const handlers = {
    handleEdit: (row) => {
      if (!row) return;
      console.log(row, "row chck");
      setFormDialogData(row);
      setHighSeaSellerId(row.id);
      setIsEditing(true);
      setShowForm(true);
    },
    handleRemove: async (row) => {
      try {
        await handleDelete(row);
      } catch (err) {
        console.error("Failed to delete:", err);
      }
    },

    handleView: (row) => {
      if (!row) return;
      const fileObjectKey = Object.keys(row).find(
        (key) =>
          row[key] &&
          typeof row[key] === "object" &&
          row[key].filePath &&
          row[key].fileName
      );

      if (fileObjectKey) {
        const fileData = row[fileObjectKey];
        try {
          const fileUrl = `${import.meta.env.VITE_BASE_FILE_URL}${fileData.filePath}`;
          window.open(encodeURI(fileUrl), "_blank");
        } catch (err) {
          console.error("View failed:", err);
        }
        return;
      }
    },
  };

  const visibleFieilds = [
    { key: "srNo", label: "Sr No", width: "70px" },
    { key: "importerHighSeaSellerName", label: "Seller Name", width: "120px" },
    { key: "importerHighSeaSellerCity", label: "City", width: "120px" },
    { key: "importerHighSeaSellerCountry", label: "Country", width: "180px" },
  ];

  // 🟡 Handle Value Change

  // 🗑️ Delete Row
  const handleDelete = async (row) => {
    try {
      setIsDeleting(true);
      const res = await deleteFn({ id: row.id }).unwrap();

      if (res?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {res?.message || "Deleted successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: LuCheck,
        });
        setShowSuccessDialog(true);

        // ✅ Just refetch data after successful delete
        await refetchHighSeaSeller();
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {err?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // ➕ Add Row
  const handleAddRow = () => {
    setShowForm(true);
  };

  // 💾 Save (Create + Update)
  const handleSave = async () => {
    setLoading(true);
    try {
      console.log("rows to send to API", formData);
      const filledData = formRef.current ? await formRef.current.submit() : {};
      console.log("filledData", filledData);

      if (!Object.keys(filledData).length) return;

      const updatedData = Object.fromEntries(
        Object.entries(filledData).map(([key, val]) => [key, val?.id ?? val])
      );

      let response;
      if (isEditing && highSeaSellerId) {
        response = await update({
          id: importerMasterId,
          subId: highSeaSellerId,
          ...updatedData,
        }).unwrap();
      } else {
        response = await create({
          id: importerMasterId,
          ...updatedData,
        }).unwrap();
      }

      console.log("response", response);

      // ✅ Check API response before showing success
      if (response?.statusCode === 200) {
        setSuccessDialogConfig({
          label: "Success",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response.message || "Data saved successfully"}
            </strong>
          ),
          buttonText: "OK",
          IconComponent: LuCheck,
        });
      } else {
        setSuccessDialogConfig({
          label: "Failure",
          title: "",
          subtitle: (
            <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
              {response?.message || "Save failed, please try again"}
            </strong>
          ),
          buttonText: "OK",
          status: "error",
          icon: BiSolidError,
        });
      }

      setShowSuccessDialog(true);
    } catch (error) {
      console.error("❌ Save failed:", error);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {error?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
      setShowForm(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        maxWidth: "100vw",
        height: 400,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 2,
          m: 0.5,
        }}
      >
        <SectionHeader title={"High Sea Seller"} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            gap: 4,
            alignItems: "center",
            pr: 2,
            m: 1,
          }}
        >
          <BlackButton
            variant="contained"
            color="primary"
            onClick={handleAddRow}
            label="Add"
            icon="LuPlus"
            sx={{ border: "none", width: 100, mb: 0 }}
          />
          <LuX size={20} style={{ cursor: "pointer" }} onClick={onClose} />
        </Box>
      </Box>
      <Divider />

      {/* Add Button */}

      {/* Table */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {isFetching && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        )}
        {!isFetching && (
          <TableGenerator
            compKey="highSeaSellerData"
            visibleFields={visibleFieilds}
            label="High Sea seller"
            handlers={handlers}
          />
        )}
      </Box>

      {/* Footer */}
      {/* <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-end",
          borderTop: "1px solid var(--gray-400)",
        }}
      >
        <OutLinedButton
          variant="contained"
          color="secondary"
          onClick={onClose}
          label="Cancel"
          sx={{ width: 100, mt: 3 }}
        />
        <BlackButton
          variant="contained"
          color="secondary"
          onClick={handleSave}
          label={loading ? "Saving..." : "Save"}
          disabled={loading || isSaveDisabled}
          sx={{ mr: 5, width: 100, mt: 3 }}
        />
      </Box> */}

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        {...successDialogConfig}
      />
      {showForm && (
        <FormDialog
          title={isEditing ? `Edit High Sea Seller` : `Add High Sea Seller`}
          schema={HighSeaSellerSchema}
          initialData={formDialogData}
          isEditing={isEditing}
          onSubmit={() => handleSave("table")}
          handleActions={handleActions}
          onClose={handleCloseForm}
          showCancelButton={true}
          showSubmitButton={true}
          cancelLabel="Cancel"
          submitLabel={isEditing ? "Save" : "Add"}
          loading={loading}
          ref={formRef}
          // disabled={!isRequiredFieldFilled}
        />
      )}
    </Box>
  );
}
